import React from 'react';
import { Link } from 'react-router-dom';
import api, { resolveMediaUrl } from '../services/api';
import { getProjectStage } from '../utils/projectStage';
import { toast } from '../utils/toastBus';

export default function ProjectCard({ project, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const [assets, setAssets] = React.useState({ images: [], audio: [], videos: [], music: [] });
  const [editing, setEditing] = React.useState(false);
  const [tempName, setTempName] = React.useState(project?.name || '');
  const [tempPrompt, setTempPrompt] = React.useState(project?.prompt || '');
  const [preview, setPreview] = React.useState(null);

  async function call(fn) {
    try {
      setBusy(true);
      const info = (msg) => { try { toast({ type: 'info', message: msg }); } catch {} };
      const ok = (msg) => { try { toast({ type: 'success', message: msg }); } catch {} };
      const err = (msg) => { try { toast({ type: 'error', message: msg }); } catch {} };
      info('Task started…');
      const r = await fn();
      if (r && (r.error || r.ok === false)) {
        const code = r.status || r.code || '';
        err(`Action failed${code ? ` (${code})` : ''}${r.error ? `: ${r.error}` : ''}`);
      } else {
        ok('Task completed');
      }
      await loadAssets();
    } catch (e) {
      try { toast({ type: 'error', message: 'Network or server error' }); } catch {}
    } finally {
      setBusy(false);
      if (onChanged) onChanged();
    }
  }

  const stage = getProjectStage(project);
  const counts = stage.counts;
  const contextSnippet = (() => {
    try {
      if (Array.isArray(project?.context)) return project.context.join('\n').slice(0, 140);
      if (typeof project?.context === 'string') return project.context.slice(0, 140);
      return '';
    } catch { return ''; }
  })();
  const pid = project?.id;

  async function loadAssets() {
    if (!pid) return;
    try {
      const j = await api.get(`/api/composer/projects/${encodeURIComponent(String(pid))}/assets`);
      if (j && j.error) {
        try { toast({ type: 'error', message: `Assets unavailable: ${j.error}` }); } catch {}
      } else if (j && j.ok === false) {
        try { toast({ type: 'error', message: 'Assets unavailable (permissions)' }); } catch {}
      }
      const mapAsset = (item, type) => {
        const raw = item?.url || item?.path || item?.audioPath || item?.videoUrl || '';
        const resolved = resolveMediaUrl(raw);
        if (type === 'audio' && !resolved && raw && raw.startsWith('/api/streams/')) {
          const filename = raw.slice('/api/streams/'.length);
          return { ...item, url: resolveMediaUrl(`/uploads/${filename}`) };
        }
        return { ...item, url: resolved || raw };
      };

      setAssets({
        images: Array.isArray(j?.items?.images) ? j.items.images.map((img) => mapAsset(img, 'image')) : [],
        audio: Array.isArray(j?.items?.audio) ? j.items.audio.map((aud) => mapAsset(aud, 'audio')) : [],
        videos: Array.isArray(j?.items?.videos) ? j.items.videos.map((vid) => mapAsset(vid, 'video')) : [],
        music: Array.isArray(j?.items?.music) ? j.items.music.map((mus) => mapAsset(mus, 'music')) : [],
      });
    } catch (e) {
      try { toast({ type: 'error', message: 'Assets failed to load' }); } catch {}
      setAssets({ images: [], audio: [], videos: [], music: [] });
    }
  }

  React.useEffect(() => { loadAssets(); }, [pid]);

  const hasAudio = (assets.audio?.length || 0) > 0;
  const hasImage = (assets.images?.length || 0) > 0;
  const hasVideo = (assets.videos?.length || 0) > 0;

  const staticDisabledReason = (!hasAudio || !hasImage) ? (!hasAudio && !hasImage ? 'Requires at least one audio and one image' : (!hasAudio ? 'Requires at least one audio' : 'Requires at least one image')) : '';
  const dynamicDisabledReason = staticDisabledReason;
  const finalizeDisabledReason = !hasVideo ? 'Requires at least one video' : '';

  async function saveName() {
    try {
      const newName = String(tempName || '').trim();
      if (!newName || newName === project?.name) { setEditing(false); setTempName(project?.name || ''); return; }
      setBusy(true);
      const r = await api.composerUpdateProject(project.id, { name: newName });
      if (r?.error) { try { toast({ type: 'error', message: 'Rename failed' }); } catch {} }
      else { try { toast({ type: 'success', message: 'Renamed' }); } catch {} ; if (onChanged) onChanged(); }
    } finally {
      setBusy(false);
      setEditing(false);
    }
  }

  async function savePrompt() {
    try {
      const newPrompt = String(tempPrompt || '').trim();
      setBusy(true);
      const r = await api.composerUpdateProject(project.id, { prompt: newPrompt });
      if (r?.error) { try { toast({ type: 'error', message: 'Prompt save failed' }); } catch {} }
      else { try { toast({ type: 'success', message: 'Prompt saved' }); } catch {} ; if (onChanged) onChanged(); }
    } finally { setBusy(false); }
  }

  async function doAudio() {
    return await api.composerCreateAudio(project.id).then((r)=>{ if (r?.ok && r?.item?.url) setPreview({ type: 'audio', url: r.item.url }); return r; });
  }
  async function doImage() {
    return await api.composerCreateImage(project.id).then((r)=>{ if (r?.ok && r?.item?.url) setPreview({ type: 'image', url: r.item.url }); return r; });
  }
  async function doStaticVideo() {
    return await api.composerCreateAudioImage(project.id).then((r)=>{ if (r?.ok && r?.item?.url) setPreview({ type: 'video', url: r.item.url }); return r; });
  }
  async function doDynamicVideo() {
    return await api.composerCreateVideo(project.id).then((r)=>{ if (r?.ok && r?.item?.url) setPreview({ type: 'video', url: r.item.url }); return r; });
  }
  async function doFinalize() {
    return await api.composerFinalize(project.id).then((r)=>{ if (r?.ok && r?.item?.url) setPreview({ type: 'video', url: r.item.url }); return r; });
  }

  async function archive() {
    try {
      if (!project?.id) return;
      const ok = typeof window !== 'undefined' ? window.confirm('Archive this project?') : true;
      if (!ok) return;
      setBusy(true);
      const r = await api.composerDeleteProject(project.id);
      if (r?.error) { try { toast({ type: 'error', message: 'Archive failed' }); } catch {} }
      else { try { toast({ type: 'success', message: 'Archived' }); } catch {} ; if (onChanged) onChanged(); }
    } finally { setBusy(false); }
  }

  return (
    <div style={{
      background: '#0f1115', border: '1px solid #1f2937', borderRadius: 12, padding: 16,
      display: 'flex', flexDirection: 'column', gap: 12
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#e5e7eb' }}>
            {editing ? (
              <input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') { setEditing(false); setTempName(project?.name || ''); } }}
                autoFocus
                disabled={busy}
                style={{ background: '#0f1115', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '4px 8px' }}
              />
            ) : (
              <span onClick={() => { setTempName(project?.name || ''); setEditing(true); }} style={{ cursor: 'text' }}>
                {project?.name || 'Project'}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'pre-wrap' }}>{contextSnippet}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <span
            style={{
              background: stage.accent,
              color: '#0b0f17',
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase'
            }}
          >
            {stage.label}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#ff7a45', fontSize: 12 }}>
            <span>🎙 {counts.audio}</span>
            <span>🖼 {counts.images}</span>
            <span>🎞 {counts.videos}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Link to={`/projects/${encodeURIComponent(String(pid))}`} style={{ ...btn('#374151'), textDecoration: 'none', display: 'inline-block' }}>
          {stage.nextAction === 'Open Project' ? 'Open' : stage.nextAction}
        </Link>
        <button disabled={busy}
          onClick={() => call(() => doAudio())}
          style={btn('#ff5a1f')}>Generate Audio</button>
        <button disabled={busy}
          onClick={() => call(() => doImage())}
          style={btn('#374151')}>Generate Image</button>
        <button disabled={busy || !hasAudio || !hasImage}
          title={staticDisabledReason}
          onClick={() => call(() => doStaticVideo())}
          style={btn('#1f2937')}>Static Video</button>
        <button disabled={busy || !hasAudio || !hasImage}
          title={dynamicDisabledReason}
          onClick={() => call(() => doDynamicVideo())}
          style={btn('#111827')}>Dynamic Video</button>
        <button disabled={busy || !hasVideo}
          title={finalizeDisabledReason}
          onClick={() => call(() => doFinalize())}
          style={btn('#0ea5e9')}>Finalize (duck)</button>
        <button disabled={busy}
          onClick={archive}
          style={btn('#dc2626')}>Archive</button>
      </div>
      {(staticDisabledReason || finalizeDisabledReason) && (
        <div style={{ color: '#9ca3af', fontSize: 12 }}>
          {!hasAudio && !hasImage && <div>Static/Dynamic Video: Requires at least one audio and one image.</div>}
          {hasAudio && !hasImage && <div>Static/Dynamic Video: Requires at least one image.</div>}
          {!hasAudio && hasImage && <div>Static/Dynamic Video: Requires at least one audio.</div>}
          {!hasVideo && <div>Finalize: Requires at least one video.</div>}
        </div>
      )}

      {/* Previews */}
      <div style={{ display: 'grid', gap: 8 }}>
        {/* Image preview */}
        {assets.images?.[0]?.url && (
          <img src={assets.images[0].url} alt="cover" style={{ width: '100%', borderRadius: 8, border: '1px solid #1f2937' }} />
        )}
        {/* Audio preview */}
        {assets.audio?.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            {assets.audio.map((aud, idx) => {
              const label = aud?.name || aud?.filename || (aud?.url ? decodeURIComponent(aud.url.split('/').pop() || '') : `Audio ${idx + 1}`);
              return (
                <div key={aud?.id || aud?.assetId || `${pid}-audio-${idx}`} style={{ display: 'grid', gap: 4 }}>
                  <audio controls src={aud?.url} preload="metadata" style={{ width: '100%' }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#9ca3af', fontSize: 12 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    {aud?.url && (
                      <a href={aud.url} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Open</a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Video preview */}
        {assets.videos?.[0]?.url && (
          <video controls src={assets.videos[0].url} style={{ width: '100%', borderRadius: 8, border: '1px solid #1f2937' }} />
        )}
        {/* Music preview */}
        {project.musicPath && !assets.audio?.length && (
          <audio controls src={project.musicPath} style={{ width: '100%' }} />
        )}
      </div>

      {preview && (
        <div onClick={()=>setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e)=>e.stopPropagation()} style={{ background: '#0f1115', border: '1px solid #374151', borderRadius: 10, padding: 12, maxWidth: '90vw', maxHeight: '84vh', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {preview.type === 'image' && (
              <img src={preview.url} alt="generated" style={{ maxWidth: '80vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
            )}
            {preview.type === 'audio' && (
              <audio controls autoPlay src={preview.url} style={{ width: '70vw' }} />
            )}
            {preview.type === 'video' && (
              <video controls autoPlay src={preview.url} style={{ maxWidth: '80vw', maxHeight: '70vh', borderRadius: 8 }} />
            )}
            <button onClick={()=>setPreview(null)} style={btn('#4b5563')}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function btn(bg) {
  return {
    background: bg, color: '#fff', border: 0, borderRadius: 8, padding: '8px 12px',
    cursor: 'pointer', fontSize: 13
  };
}
